import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Button, Avatar, Dropdown } from 'antd';
import {
  DashboardOutlined,
  TrophyOutlined,
  TeamOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
  SettingOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  MessageOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SiteFooter from './SiteFooter';

const { Header, Sider, Content } = AntLayout;

const Layout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, features, logout } = useAuth();
  const isAdmin = user?.admin === true || features?.admin === true;
  const isClubOwner = user?.clubOwner === true || features?.clubOwner === true;
  const canCompetitionSetup = user?.competitionSetup === true || features?.competitionSetup === true || isAdmin;

  // Check window size for mobile responsiveness
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-collapse sidebar on mobile
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const baseMenuItems = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: '/dashboard/competitions', icon: <TrophyOutlined />, label: 'Competitions' },
    { key: '/dashboard/athletes', icon: <TeamOutlined />, label: 'Athletes' },
    { key: '/dashboard/results', icon: <SearchOutlined />, label: 'Results' },
    { key: '/dashboard/chat', icon: <MessageOutlined />, label: 'TrackiAI' },
    { key: '/dashboard/scrape-urls', icon: <GlobalOutlined />, label: 'Submit URLs' },
  ];

  const menuItems = [
    ...baseMenuItems,
    ...(canCompetitionSetup
      ? [{ key: '/dashboard/competition-setup', icon: <ToolOutlined />, label: 'Live Setup' }]
      : []),
    ...(isClubOwner || isAdmin
      ? [{ key: '/dashboard/club-management', icon: <ApartmentOutlined />, label: 'Club Management' }]
      : []),
    ...(isAdmin ? [{ key: '/dashboard/admin', icon: <SafetyCertificateOutlined />, label: 'Admin' }] : []),
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <SettingOutlined />,
      label: 'Profile',
      onClick: () => navigate('/dashboard/profile'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  return (
    <AntLayout className="layout-root" style={{ minHeight: '100vh', background: 'transparent' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        collapsedWidth={isMobile ? 0 : 80}
        width={isMobile ? 200 : 220}
        className="layout-sider"
        style={{ overflow: 'hidden', borderRight: '1px solid var(--border-subtle)' }}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate('/')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/')}
          style={{
            height: isMobile ? 48 : 44,
            margin: isMobile ? 12 : 16,
            background: 'var(--surface-card-soft)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 10,
            padding: collapsed ? 0 : '0 12px',
            color: 'var(--text-primary)',
            fontWeight: 600,
            fontSize: isMobile ? 14 : undefined,
            cursor: 'pointer',
          }}
        >
          <img
            src="/logo512.png"
            alt="TrackiAI logo"
            style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
          />
          {!collapsed && <span>Tracki.AI</span>}
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
          inlineIndent={isMobile ? 16 : 24}
        />
      </Sider>
      <AntLayout className="layout-inner">
        <Header
          className="layout-header-bar"
          style={{
            padding: isMobile ? '0 8px' : '0 24px',
            background: 'var(--surface-page)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid var(--border-subtle)',
            minHeight: isMobile ? 48 : 64,
            height: isMobile ? 48 : 64,
            lineHeight: isMobile ? '48px' : '64px',
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: isMobile ? 18 : 16,
              width: isMobile ? 44 : 64,
              height: isMobile ? 44 : 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              flex: 1,
              minWidth: 0,
            }}
          >
            <span
              style={{
                display: isMobile ? 'none' : 'inline',
                fontSize: 14,
                color: 'var(--text-secondary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Welcome, {user?.email}
            </span>
            <span
              style={{
                display: !isMobile ? 'none' : 'inline',
                fontSize: 12,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '40vw',
              }}
            >
              {user?.email?.split('@')[0] || 'User'}
            </span>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
              <Avatar
                icon={<UserOutlined />}
                size={isMobile ? 'small' : 'default'}
                style={{ cursor: 'pointer', flexShrink: 0 }}
              />
            </Dropdown>
          </div>
        </Header>
        <Content
          className="layout-main-content"
          style={{
            margin: isMobile ? '12px 8px' : '24px 16px',
            padding: isMobile ? 12 : 24,
            minHeight: 280,
            borderRadius: 6,
            border: '1px solid var(--border-subtle)',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            overflowX: 'hidden',
            minWidth: 0,
          }}
        >
          <Outlet />
        </Content>
        <SiteFooter />
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
